/**
 * Summary Report Service
 * Handles generation of Summary Report HTML for preview and PDF generation
 * Also provides API functions for fetching and submitting Summary Reports
 */

import api from '../utils/api';

// API Service functions
export const summaryReportService = {
  // Get Summary Report by appointment ID
  getReportByAppointmentId: async (appointmentId) => {
    try {
      console.log(`Calling GET /admin/summary-reports/by-appointment/${appointmentId}`);
      const response = await api.get(`/admin/summary-reports/by-appointment/${appointmentId}`);
      console.log('GET response:', response);
      return response.data;
    } catch (error) {
      console.error('Error fetching Summary Report:', error);
      throw error;
    }
  },
  
  // Create a new Summary Report
  createReport: async (reportData) => {
    try {
      const pdfUrl = reportData.pdf_url;
      console.log('PDF URL from reportData:', pdfUrl);
      console.log('Goals status from reportData:', reportData.goals_status);
      
      const cleanData = {
        ...reportData,
        pdf_url: pdfUrl,
        // Explicitly preserve goals_status
        goals_status: reportData.goals_status
      };
      
      const payload = {
        data: cleanData
      };
      
      console.log('Full payload JSON:', JSON.stringify(payload));
      console.log('PDF URL in payload:', payload.data.pdf_url);
      console.log('Goals status in payload:', payload.data.goals_status);
      
      console.log('Calling POST /admin/summary-reports');
      const response = await api.post('/admin/summary-reports', payload);
      
      console.log('POST response status:', response.status);
      console.log('POST response data:', response.data);
      
      return response.data;
    } catch (error) {
      console.error('Error creating Summary Report:', error);
      throw error;
    }
  },
  
  // Update an existing Summary Report
  updateReport: async (reportId, reportData) => {
    try {
      console.log(`Calling PUT /admin/summary-reports/${reportId} with data:`, reportData);
      const response = await api.put(`/admin/summary-reports/${reportId}`, { data: reportData });
      console.log('PUT response:', response);
      return response.data;
    } catch (error) {
      console.error('Error updating Summary Report:', error);
      throw error;
    }
  },
  
  // Fetch recommendations from external API
  fetchRecommendations: async (sidNo, visitDate, branchId) => {
    try {
      console.log('Fetching recommendations from external API');
      
      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('API timeout after 60 seconds')), 60000)
      );
      
      const fetchPromise = fetch('https://kwr7scabbu.ap-south-1.awsapprunner.com/get-medical-condition_combined', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sid_no: sidNo,
          visit_date: visitDate,
          branch_id: branchId
        })
      }).then(res => {
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return res.json();
      });
      
      // Race between fetch and timeout
      const response = await Promise.race([fetchPromise, timeoutPromise]);
      console.log('Recommendations fetched:', response);
      
      // Extract recommendations from nested structure
      if (response && response.recommendations) {
        return {
          data: response.recommendations,
          source: 'api',
          error: null
        };
      }
      return {
        data: response,
        source: 'api',
        error: null
      };
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      
      // Parse error details
      let errorMessage = error.message;
      let statusCode = null;
      let apiDetail = null;
      
      // Try to extract error details from response
      if (error.response) {
        statusCode = error.response.status;
        apiDetail = error.response.data?.detail || error.response.data?.message;
      }
      
      // Return sample data as fallback with error info
      console.log('Using sample data as fallback');
      return {
        data: {
          Nutrition: [
            "Limit high sodium food",
            "Prioritize Lean Protein Sources (Meat & Poultry)",
            "Regular Consumption of Legumes (Peas, Lentils, Beans)",
            "Daily Vitamin D3 (2000 IU) & K2 Supplementation",
            "Prioritize Whole Grains",
            "Increase soluble fiber intake",
            "Minimize Refined Carbohydrates & Sugary Beverage Consumption"
          ],
          Exercise: [],
          Lifestyle: [
            "Stay hydrated",
            "Proactive Stress Management Techniques"
          ],
          Supplement: [
            "Daily Psyllium Husk Fiber Supplementation"
          ]
        },
        source: 'fallback',
        error: {
          message: errorMessage,
          statusCode: statusCode,
          detail: apiDetail
        }
      };
    }
  },
  
  // Fetch all tactics
  fetchAllTactics: async () => {
    try {
      console.log('Fetching all tactics');
      const response = await api.get('/admin/tactics');
      console.log('Tactics fetched:', response.data);
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching tactics:', error);
      throw error;
    }
  }
};

/**
 * Generate HTML for Summary Report preview
 * @param {Object} reportData - The Summary Report data
 * @returns {Promise<string>} - HTML content for the report
 */
export const generateSummaryReportHTML = async (reportData) => {
  try {
    console.log("Summary Report reportData", reportData);
    
    // Fetch the template file
    const response = await fetch('/templates/summary_report.html');
    
    if (!response.ok) {
      throw new Error(`Failed to fetch template: ${response.status}`);
    }
    
    let template = await response.text();
    
    // Helper function to format date as "2ND APRIL 2025"
    function formatDate(dateString) {
      if (!dateString || dateString.trim() === '' || isNaN(Date.parse(dateString))) {
        return 'N/A';
      }
      
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      
      const day = date.getDate();
      const month = date.toLocaleString('en-US', { month: 'long' }).toUpperCase();
      const year = date.getFullYear();
      
      const suffix = getDaySuffix(day);
      
      return `${day}${suffix} ${month} ${year}`;
    }
    
    // Helper function to get the day suffix
    function getDaySuffix(day) {
      if (day > 3 && day < 21) return 'TH';
      switch (day % 10) {
        case 1: return 'ST';
        case 2: return 'ND';
        case 3: return 'RD';
        default: return 'TH';
      }
    }
    
    let processedTemplate = template;
    
    // Helper function to format tactics data - generates proper HTML structure matching template
    const formatTacticsSection = (tactics, category) => {
      if (!tactics || tactics.length === 0) return '';
      
      // Determine card class based on category
      let cardClass = 'tactic-card';
      let iconClass = 'icon';
      let biomarkersClass = 'biomarkers-impacted';
      let headerClass = 'impact-header';
      
      if (category === 'Exercise') {
        cardClass = 'tactic-card tactic-card1';
        iconClass = 'icon icon2';
        biomarkersClass = 'biomarkers-impacted biomarkers-impacted1';
        headerClass = 'impact-header impact-header1';
      } else if (category === 'Supplementation') {
        cardClass = 'tactic-card tactic-card2';
        iconClass = 'icon icon3';
        biomarkersClass = 'biomarkers-impacted biomarkers-impacted2';
        headerClass = 'impact-header impact-header2';
      } else if (category === 'Lifestyle') {
        cardClass = 'tactic-card tactic-card3';
        iconClass = 'icon icon4';
        biomarkersClass = 'biomarkers-impacted biomarkers-impacted3';
        headerClass = 'impact-header impact-header3';
      }
      
      let html = `<section class="nutrition-section">
            <h2>${category.toUpperCase()}</h2>`;
      
      tactics.forEach(tactic => {
        if (tactic.name) { // Only render if tactic has a name
          html += `
            <div class="${cardClass}">
                <div class="tactic-details">
                    <div class="${iconClass}">🍴</div>
                    <div class="description-content">
                        <h3 class="title">${tactic.name || ''}</h3>
                        <p class="example">${tactic.description || ''}</p>
                        <p class="benefit">${tactic.description || ''}</p>
                    </div>
                </div>
                <div class="${biomarkersClass}">
                    <div class="${headerClass}">
                        <span class="impact-count">${tactic.biomarkers?.length || 0}</span> BIOMARKERS IMPACTED
                    </div>
                    <ul class="impact-list">
                        <li>${tactic.biomarkers ? tactic.biomarkers.join(', ') : ''}</li>
                    </ul>
                </div>
            </div>`;
        }
      });
      
      html += `
        </section>`;
      
      return html;
    };
    
    // Replace all placeholders with actual data
    const replacements = {
      // Patient Information
      '{{patient_name}}': reportData.patient_name || 'N/A',
      '{{patient_age}}': reportData.patient_age || 'N/A',
      '{{patient_gender}}': reportData.patient_gender || 'N/A',
      '{{patient_id}}': reportData.patient_id || 'N/A',
      '{{report_id}}': reportData.report_id || reportData.patient_id || 'N/A',
      '{{exam_date}}': formatDate(reportData.exam_date) || 'N/A',
      
      // Overview section
      '{{biomarkers_tested}}': reportData.biomarkers_tested || '0',
      '{{biomarkers_in_range}}': reportData.biomarkers_in_range || '0',
      '{{biomarkers_of_concern}}': reportData.biomarkers_of_concern || '0',
      '{{overview_summary}}': reportData.overview_summary || '',
      '{{overview_description}}': reportData.overview_description || '',
      
      // Tactics sections
      '{{nutrition_tactics}}': formatTacticsSection(reportData.nutrition_tactics, 'Nutrition'),
      '{{exercise_tactics}}': formatTacticsSection(reportData.exercise_tactics, 'Exercise'),
      '{{supplementation_tactics}}': formatTacticsSection(reportData.supplementation_tactics, 'Supplementation'),
      '{{lifestyle_tactics}}': formatTacticsSection(reportData.lifestyle_tactics, 'Lifestyle'),

      '{{vo2_max}}': reportData.goals_status.biomarker_status[0].current || 'N/A',
      '{{vo2_max_percentile}}': reportData.goals_status.biomarker_status[0].percentile || 'N/A',
      '{{vo2_max_goal}}': reportData.goals_status.biomarker_status[0].goal || 'N/A',

      '{{body_fat}}': reportData.goals_status.biomarker_status[1].current || 'N/A',
      '{{body_fat_percentile}}': reportData.goals_status.biomarker_status[1].percentile || 'N/A',
      '{{body_fat_goal}}': reportData.goals_status.biomarker_status[1].goal || 'N/A',

      '{{aerobic_threshold}}': reportData.goals_status.biomarker_status[2].current || 'N/A',
      '{{aerobic_threshold_percentile}}': reportData.goals_status.biomarker_status[2].percentile || 'N/A',
      '{{aerobic_threshold_goal}}': reportData.goals_status.biomarker_status[2].goal || 'N/A',

      '{{insulin}}': reportData.goals_status.biomarker_status[3].current || 'N/A',
      '{{insulin_percentile}}': reportData.goals_status.biomarker_status[3].percentile || 'N/A',
      '{{insulin_goal}}': reportData.goals_status.biomarker_status[3].goal || 'N/A',

      '{{homa_ir}}': reportData.goals_status.biomarker_status[4].current || 'N/A',
      '{{homa_ir_percentile}}': reportData.goals_status.biomarker_status[4].percentile || 'N/A',
      '{{homa_ir_goal}}': reportData.goals_status.biomarker_status[4].goal || 'N/A',
      
      '{{goal_category}}': reportData.goals_status.goal_name || 'N/A',
      
      // Additional sections
      '{{recommendations}}': reportData.recommendations || '',
      '{{notes}}': reportData.notes || '',
    };
    
    // Replace all placeholders in the template
    Object.entries(replacements).forEach(([key, value]) => {
      template = template.replace(new RegExp(key, 'g'), value);
    });
    
    return template;
  } catch (error) {
    console.error('Error generating Summary Report:', error);
    throw error;
  }
};
